import React, {useMemo} from 'react';
import {defineMessages, useIntl} from 'react-intl';
import type {DataGridColumn, DataGridServerProps, TextColumn} from '@igp/shared/components/data-grid';
import {DataGridServer} from '@igp/shared/components/data-grid';
import {formatTimestamp} from '@igp/shared/utils';

import type {CountryLevelPartnerReportViewModel, CountryLevelPartnerReportViewModelKeys} from '../types';

export const localized = defineMessages({
    date_ts: {
        id: 'CountryLevelPartnerReportColumn_date',
        defaultMessage: 'Date',
    },
    country: {
        id: 'CountryLevelPartnerReportColumn_country',
        defaultMessage: 'Country',
    },
    poker_cash_ggr: {
        id: 'CountryLevelPartnerReportColumn_poker_cash_ggr',
        defaultMessage: 'Poker Cash GGR',
    },
    poker_mtt_ggr: {
        id: 'CountryLevelPartnerReportColumn_poker_mtt_ggr',
        defaultMessage: 'Poker MTT GGR',
    },
    global_spins_ggr: {
        id: 'CountryLevelPartnerReportColumn_global_spins_ggr',
        defaultMessage: 'Spins GGR',
    },
    casino_ggr: {
        id: 'CountryLevelPartnerReportColumn_casino_ggr',
        defaultMessage: 'Casino GGR',
    },
    sports_ggr: {
        id: 'CountryLevelPartnerReportColumn_sports_ggr',
        defaultMessage: 'Sports GGR',
    },
    total_ggr: {
        id: 'CountryLevelPartnerReportColumn_total_ggr',
        defaultMessage: 'Total GGR',
    },
    new_users: {
        id: 'CountryLevelPartnerReportColumn_new_users',
        defaultMessage: 'New Users',
    },
    daily_active_users: {
        id: 'CountryLevelPartnerReportColumn_daily_active_users',
        defaultMessage: 'Daily Active Users',
    },
    total_hands: {
        id: 'CountryLevelPartnerReportColumn_total_hands',
        defaultMessage: 'Total Hands',
    },
    total_mtt_entries: {
        id: 'CountryLevelPartnerReportColumn_total_mtt_entries',
        defaultMessage: 'Total MTT Entries',
    },
    total_bet_amount: {
        id: 'CountryLevelPartnerReportColumn_total_bet_amount',
        defaultMessage: 'Total Bet Amount',
    },
    payout_amount: {
        id: 'CountryLevelPartnerReportColumn_payout_amount',
        defaultMessage: 'Payout Amount',
    },
    first_time_deposit_users: {
        id: 'CountryLevelPartnerReportColumn_first_time_deposit_users',
        defaultMessage: 'First-Time Depositors',
    },
    first_deposit_amount: {
        id: 'CountryLevelPartnerReportColumn_first_deposit_amount',
        defaultMessage: 'First Deposit Amount',
    },
    retained_deposit_users: {
        id: 'CountryLevelPartnerReportColumn_retained_deposit_users',
        defaultMessage: 'Retained Deposit Users',
    },
    retained_deposit_amount: {
        id: 'CountryLevelPartnerReportColumn_retained_deposit_amount',
        defaultMessage: 'Retained Deposit Amount',
    },
    deposit_users: {
        id: 'CountryLevelPartnerReportColumn_deposit_users',
        defaultMessage: 'Deposit Users',
    },
    deposit_count: {
        id: 'CountryLevelPartnerReportColumn_deposit_count',
        defaultMessage: 'Deposit Count',
    },
    total_deposit_amount: {
        id: 'CountryLevelPartnerReportColumn_total_deposit_amount',
        defaultMessage: 'Total Deposit Amount',
    },
    withdrawal_users: {
        id: 'CountryLevelPartnerReportColumn_withdrawal_users',
        defaultMessage: 'Withdrawal Users',
    },
    withdrawal_count: {
        id: 'CountryLevelPartnerReportColumn_withdrawal_count',
        defaultMessage: 'Withdrawal Count',
    },
    total_withdrawal_amount: {
        id: 'CountryLevelPartnerReportColumn_total_withdrawal_amount',
        defaultMessage: 'Total Withdrawal Amount',
    },
    net_deposit: {
        id: 'CountryLevelPartnerReportColumn_net_deposit',
        defaultMessage: 'Net Deposit',
    },
    promotional_spend: {
        id: 'CountryLevelPartnerReportColumn_promotional_spend',
        defaultMessage: 'Promotional Spend',
    },
});

export function useCountryLevelPartnerReportColumns(
    keys: CountryLevelPartnerReportViewModelKeys[]
): DataGridColumn<CountryLevelPartnerReportViewModel>[] {
    const {formatNumber} = useIntl();
    const currency = 'USD';

    const dateTs: TextColumn<CountryLevelPartnerReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => formatTimestamp(m.date_ts, 'date'),
        field: 'date_ts',
        title: localized.date_ts,
        sortable: true,
    };

    const country: TextColumn<CountryLevelPartnerReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => m.country,
        field: 'country',
        title: localized.country,
        sortable: true,
    };

    const pokerCashGgr: TextColumn<CountryLevelPartnerReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => formatNumber(m.poker_cash_ggr, {style: 'currency', currency}),
        field: 'poker_cash_ggr',
        title: localized.poker_cash_ggr,
        sortable: true,
    };

    const pokerMttGgr: TextColumn<CountryLevelPartnerReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => formatNumber(m.poker_mtt_ggr, {style: 'currency', currency}),
        field: 'poker_mtt_ggr',
        title: localized.poker_mtt_ggr,
        sortable: true,
    };

    const globalSpinsGgr: TextColumn<CountryLevelPartnerReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => formatNumber(m.global_spins_ggr, {style: 'currency', currency}),
        field: 'global_spins_ggr',
        title: localized.global_spins_ggr,
        sortable: true,
    };

    const casinoGgr: TextColumn<CountryLevelPartnerReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => formatNumber(m.casino_ggr, {style: 'currency', currency}),
        field: 'casino_ggr',
        title: localized.casino_ggr,
        sortable: true,
    };

    const sportsGgr: TextColumn<CountryLevelPartnerReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => formatNumber(m.sports_ggr, {style: 'currency', currency}),
        field: 'sports_ggr',
        title: localized.sports_ggr,
        sortable: true,
    };

    const totalGgr: TextColumn<CountryLevelPartnerReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => formatNumber(m.total_ggr, {style: 'currency', currency}),
        field: 'total_ggr',
        title: localized.total_ggr,
        sortable: true,
    };

    const newUsers: TextColumn<CountryLevelPartnerReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => m.new_users?.toString(),
        field: 'new_users',
        title: localized.new_users,
        sortable: true,
    };

    const dailyActiveUsers: TextColumn<CountryLevelPartnerReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => m.daily_active_users?.toString(),
        field: 'daily_active_users',
        title: localized.daily_active_users,
        sortable: true,
    };

    const totalHands: TextColumn<CountryLevelPartnerReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => m.total_hands?.toString(),
        field: 'total_hands',
        title: localized.total_hands,
        sortable: true,
    };

    const totalMttEntries: TextColumn<CountryLevelPartnerReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => m.total_mtt_entries?.toString(),
        field: 'total_mtt_entries',
        title: localized.total_mtt_entries,
        sortable: true,
    };

    const totalBetAmount: TextColumn<CountryLevelPartnerReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => formatNumber(m.total_bet_amount, {style: 'currency', currency}),
        field: 'total_bet_amount',
        title: localized.total_bet_amount,
        sortable: true,
    };

    const payoutAmount: TextColumn<CountryLevelPartnerReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => formatNumber(m.payout_amount, {style: 'currency', currency}),
        field: 'payout_amount',
        title: localized.payout_amount,
        sortable: true,
    };

    const firstTimeDepositUsers: TextColumn<CountryLevelPartnerReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => m.first_time_deposit_users?.toString(),
        field: 'first_time_deposit_users',
        title: localized.first_time_deposit_users,
        sortable: true,
    };

    const firstDepositAmount: TextColumn<CountryLevelPartnerReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => formatNumber(m.first_deposit_amount, {style: 'currency', currency}),
        field: 'first_deposit_amount',
        title: localized.first_deposit_amount,
        sortable: true,
    };

    const retainedDepositUsers: TextColumn<CountryLevelPartnerReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => m.retained_deposit_users?.toString(),
        field: 'retained_deposit_users',
        title: localized.retained_deposit_users,
        sortable: true,
    };

    const retainedDepositAmount: TextColumn<CountryLevelPartnerReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => formatNumber(m.retained_deposit_amount, {style: 'currency', currency}),
        field: 'retained_deposit_amount',
        title: localized.retained_deposit_amount,
        sortable: true,
    };

    const depositUsers: TextColumn<CountryLevelPartnerReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => m.deposit_users?.toString(),
        field: 'deposit_users',
        title: localized.deposit_users,
        sortable: true,
    };

    const depositCount: TextColumn<CountryLevelPartnerReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => m.deposit_count?.toString(),
        field: 'deposit_count',
        title: localized.deposit_count,
        sortable: true,
    };

    const totalDepositAmount: TextColumn<CountryLevelPartnerReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => formatNumber(m.total_deposit_amount, {style: 'currency', currency}),
        field: 'total_deposit_amount',
        title: localized.total_deposit_amount,
        sortable: true,
    };

    const withdrawalUsers: TextColumn<CountryLevelPartnerReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => m.withdrawal_users?.toString(),
        field: 'withdrawal_users',
        title: localized.withdrawal_users,
        sortable: true,
    };

    const withdrawalCount: TextColumn<CountryLevelPartnerReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => m.withdrawal_count?.toString(),
        field: 'withdrawal_count',
        title: localized.withdrawal_count,
        sortable: true,
    };

    const totalWithdrawalAmount: TextColumn<CountryLevelPartnerReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => formatNumber(m.total_withdrawal_amount, {style: 'currency', currency}),
        field: 'total_withdrawal_amount',
        title: localized.total_withdrawal_amount,
        sortable: true,
    };

    const netDeposit: TextColumn<CountryLevelPartnerReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => formatNumber(m.net_deposit, {style: 'currency', currency}),
        field: 'net_deposit',
        title: localized.net_deposit,
        sortable: true,
    };

    const promotionalSpend: TextColumn<CountryLevelPartnerReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => formatNumber(m.promotional_spend, {style: 'currency', currency}),
        field: 'promotional_spend',
        title: localized.promotional_spend,
        sortable: true,
    };

    const config: Record<CountryLevelPartnerReportViewModelKeys, DataGridColumn<CountryLevelPartnerReportViewModel>> = {
        date_ts: dateTs,
        country,
        poker_cash_ggr: pokerCashGgr,
        poker_mtt_ggr: pokerMttGgr,
        global_spins_ggr: globalSpinsGgr,
        casino_ggr: casinoGgr,
        sports_ggr: sportsGgr,
        total_ggr: totalGgr,
        new_users: newUsers,
        daily_active_users: dailyActiveUsers,
        total_hands: totalHands,
        total_mtt_entries: totalMttEntries,
        total_bet_amount: totalBetAmount,
        payout_amount: payoutAmount,
        first_time_deposit_users: firstTimeDepositUsers,
        first_deposit_amount: firstDepositAmount,
        retained_deposit_users: retainedDepositUsers,
        retained_deposit_amount: retainedDepositAmount,
        deposit_users: depositUsers,
        deposit_count: depositCount,
        total_deposit_amount: totalDepositAmount,
        withdrawal_users: withdrawalUsers,
        withdrawal_count: withdrawalCount,
        total_withdrawal_amount: totalWithdrawalAmount,
        net_deposit: netDeposit,
        promotional_spend: promotionalSpend,
    };

    return useMemo(() => {
        return keys.map(k => config[k]);
    }, [keys.join(',')]);
}

type CountryLevelPartnerReportDataGridProps = Omit<
    DataGridServerProps<CountryLevelPartnerReportViewModel, CountryLevelPartnerReportViewModelKeys>,
    'columns'
> & {columns: CountryLevelPartnerReportViewModelKeys[]};

export function CountryLevelPartnerReportDataGrid(props: CountryLevelPartnerReportDataGridProps) {
    const columns = useCountryLevelPartnerReportColumns(props.columns);
    return <DataGridServer {...props} columns={columns} />;
}
